import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";
import { lightDark } from "utils/lightDark";
import {
  browserTimeZone,
  toZonedDateTimeLocal,
  formatMeetingTime,
} from "layouts/meetings/meetingTime";

// The theme's "light" button is 40% white in dark mode, which reads as disabled on the header;
// make it solid like the other pages' header buttons.
const HEADER_BUTTON_SX = {
  backgroundColor: lightDark("light.main", "white.main"),
  "&:hover": { backgroundColor: lightDark("light.main", "white.main") },
  // Focus returns here when the dialog closes; MDButton's focus style would bring back the 40% white.
  "&:focus:not(:hover)": { backgroundColor: lightDark("light.focus", "white.main") },
};

const emptyMeetingForm = () => ({
  subject: "",
  description: "",
  startDateTime: "",
  endDateTime: "",
  timeZone: browserTimeZone(),
  attendees: "",
  meetingType: "one-time",
  recurrencePattern: "weekly",
  recurrenceInterval: 1,
  recurrenceEndDate: "",
  sendEmail: true,
});

function Meetings() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Done, but with something the admin must check (e.g. Teams moved but a record didn't).
  const [warning, setWarning] = useState("");
  // Errors from a dialog's own action show inside that dialog, not behind it on the page.
  const [dialogError, setDialogError] = useState("");
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [meetingSubmitting, setMeetingSubmitting] = useState(false);
  const [meetingForm, setMeetingForm] = useState(emptyMeetingForm());

  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  // Upcoming (soonest first) by default; past meetings (most recent first) on request.
  const [showPast, setShowPast] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    startDateTime: "",
    endDateTime: "",
    timeZone: "",
    sendEmail: true,
  });

  useEffect(() => {
    fetchMeetings();
  }, [showPast]);

  const fetchMeetings = async () => {
    try {
      setMeetingsLoading(true);
      const data = await adminApi.getTeamsMeetings({ when: showPast ? "past" : "upcoming" });
      setMeetings(Array.isArray(data) ? data : data.meetings || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load meetings");
    } finally {
      setMeetingsLoading(false);
    }
  };

  const handleCreateMeeting = async () => {
    try {
      setMeetingSubmitting(true);
      setDialogError("");
      setSuccess("");

      const payload = {
        ...meetingForm,
        attendees: meetingForm.attendees
          .split(/[;,\n]+/)
          .map((item) => item.trim())
          .filter(Boolean),
        startDateTime: meetingForm.startDateTime,
        endDateTime: meetingForm.endDateTime,
        recurrencePattern:
          meetingForm.meetingType === "recurring" ? meetingForm.recurrencePattern : undefined,
        recurrenceInterval: Number(meetingForm.recurrenceInterval) || 1,
        recurrenceEndDate: meetingForm.recurrenceEndDate || undefined,
      };

      const response = await adminApi.createTeamsMeeting(payload);
      setSuccess(`Teams meeting created${response?.meeting?.joinUrl ? ": join link ready" : ""}`);
      setMeetingDialogOpen(false);
      setMeetingForm(emptyMeetingForm());
      fetchMeetings();
    } catch (err) {
      setDialogError(err.response?.data?.message || "Failed to create Teams meeting");
    } finally {
      setMeetingSubmitting(false);
    }
  };

  const handleMenuOpen = (event, meeting) => {
    setAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openCreateDialog = () => {
    setDialogError("");
    setMeetingDialogOpen(true);
  };

  const openDeleteDialog = () => {
    setDialogError("");
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMeeting) return;
    try {
      setDeleteSubmitting(true);
      setDialogError("");
      setSuccess("");
      setWarning("");
      const response = await adminApi.deleteTeamsMeeting(selectedMeeting._id);
      // The server says when a Clarity Call booking was cancelled along with its meeting.
      if (response?.warning) setWarning(response.warning);
      else setSuccess(response?.message || "Meeting deleted");
      setDeleteDialogOpen(false);
      setSelectedMeeting(null);
      fetchMeetings();
    } catch (err) {
      setDialogError(err.response?.data?.message || "Failed to delete meeting");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const openRescheduleDialog = () => {
    if (selectedMeeting) {
      // The server reads typed times in the Time zone box, so prefill in the meeting's own zone:
      // saving without edits then sends back exactly the stored time (a no-op).
      const timeZone = selectedMeeting.timeZone || browserTimeZone();
      setRescheduleForm({
        startDateTime: toZonedDateTimeLocal(selectedMeeting.startDateTime, timeZone),
        endDateTime: toZonedDateTimeLocal(selectedMeeting.endDateTime, timeZone),
        timeZone,
        sendEmail: true,
      });
    }
    setDialogError("");
    setRescheduleDialogOpen(true);
    setAnchorEl(null);
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedMeeting) return;
    try {
      setRescheduleSubmitting(true);
      setDialogError("");
      setSuccess("");
      setWarning("");
      const response = await adminApi.rescheduleTeamsMeeting(selectedMeeting._id, rescheduleForm);
      if (response?.warning) setWarning(response.warning);
      else setSuccess(response?.message || "Meeting rescheduled");
      setRescheduleDialogOpen(false);
      setSelectedMeeting(null);
      fetchMeetings();
    } catch (err) {
      setDialogError(err.response?.data?.message || "Failed to reschedule meeting");
    } finally {
      setRescheduleSubmitting(false);
    }
  };

  const columns = [
    { Header: "subject", accessor: "subject", width: "22%", align: "left" },
    { Header: "attendees", accessor: "attendees", width: "22%", align: "left" },
    { Header: "start", accessor: "start", width: "16%", align: "left" },
    { Header: "end", accessor: "end", width: "16%", align: "left" },
    { Header: "type", accessor: "type", width: "10%", align: "center" },
    { Header: "actions", accessor: "actions", width: "14%", align: "center" },
  ];

  const rows = meetings.map((meeting) => ({
    subject: (
      <MDBox>
        <MDTypography variant="button" fontWeight="medium">
          {meeting.subject}
        </MDTypography>
        {meeting.joinUrl && (
          <MDBox mt={0.25}>
            <MDTypography
              component="a"
              href={meeting.joinUrl}
              target="_blank"
              rel="noreferrer"
              variant="caption"
              color="info"
              fontWeight="medium"
            >
              Join link
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    ),
    attendees: (
      <MDTypography variant="caption" color="text">
        {(meeting.attendees || []).join(", ") || "—"}
      </MDTypography>
    ),
    start: (
      <MDBox>
        <MDTypography variant="caption" color="text" display="block">
          {formatMeetingTime(meeting.startDateTime, meeting.timeZone)}
        </MDTypography>
        {meeting.timeZone && (
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
            {meeting.timeZone}
          </MDTypography>
        )}
      </MDBox>
    ),
    end: (
      <MDTypography variant="caption" color="text">
        {formatMeetingTime(meeting.endDateTime, meeting.timeZone)}
      </MDTypography>
    ),
    type: (
      <Chip
        size="small"
        label={meeting.meetingType === "recurring" ? "Recurring" : "One-time"}
        color={meeting.meetingType === "recurring" ? "info" : "default"}
      />
    ),
    actions: (
      <IconButton onClick={(e) => handleMenuOpen(e, meeting)}>
        <Icon>more_vert</Icon>
      </IconButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "0 8px 32px rgba(16, 185, 129, 0.15)",
                border: "1px solid rgba(16, 185, 129, 0.12)",
                transition: "all 0.3s ease",
              }}
            >
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={{ xs: 2, md: 3 }}
                sx={{
                  background: "linear-gradient(135deg, #10b981 0%, #2563eb 100%)",
                  borderRadius: "lg",
                  boxShadow: "0 8px 24px rgba(16, 185, 129, 0.3)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  gap={2}
                >
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDBox
                      sx={{
                        width: "50px",
                        height: "50px",
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                      }}
                    >
                      <Icon sx={{ color: "white", fontSize: 28 }}>video_camera_front</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="h5" fontWeight="bold" color="white">
                        Meetings
                      </MDTypography>
                      <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                        Create and manage Microsoft Teams meetings for clients and internal
                        follow-ups
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDButton color="light" onClick={openCreateDialog} sx={HEADER_BUTTON_SX}>
                    <Icon sx={{ mr: 1 }}>add</Icon>
                    New meeting
                  </MDButton>
                </MDBox>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              {warning && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="warning" dismissible onClose={() => setWarning("")}>
                    {warning}
                  </MDAlert>
                </MDBox>
              )}

              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <MDBox px={2} pt={2} display="flex" justifyContent="flex-end">
                <FormControlLabel
                  control={
                    <Switch checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
                  }
                  label={
                    showPast ? "Showing past meetings (most recent first)" : "Show past meetings"
                  }
                />
              </MDBox>

              <MDBox pt={1}>
                {meetingsLoading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2">Loading...</MDTypography>
                  </MDBox>
                ) : meetings.length === 0 ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">
                      {showPast ? "No past meetings." : "No upcoming meetings."}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Create meeting dialog */}
      <Dialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{ sx: { borderRadius: isPhone ? 0 : "16px" } }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0078d4 0%, #2b6cb0 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          Create Microsoft Teams meeting
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
              {dialogError}
            </MDAlert>
          )}
          <Stack spacing={2} mt={1}>
            <TextField
              label="Meeting title"
              fullWidth
              value={meetingForm.subject}
              onChange={(e) => setMeetingForm({ ...meetingForm, subject: e.target.value })}
            />
            <TextField
              label="Description"
              multiline
              minRows={3}
              fullWidth
              value={meetingForm.description}
              onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Start date/time"
                type="datetime-local"
                fullWidth
                value={meetingForm.startDateTime}
                onChange={(e) => setMeetingForm({ ...meetingForm, startDateTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End date/time"
                type="datetime-local"
                fullWidth
                value={meetingForm.endDateTime}
                onChange={(e) => setMeetingForm({ ...meetingForm, endDateTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Time zone"
              fullWidth
              value={meetingForm.timeZone}
              onChange={(e) => setMeetingForm({ ...meetingForm, timeZone: e.target.value })}
              helperText="The start and end times above are in this zone (e.g. Africa/Lagos, Europe/London)."
            />
            <TextField
              label="Invitee emails (comma/new line separated)"
              fullWidth
              multiline
              minRows={2}
              value={meetingForm.attendees}
              onChange={(e) => setMeetingForm({ ...meetingForm, attendees: e.target.value })}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Meeting type</InputLabel>
                <Select
                  value={meetingForm.meetingType}
                  label="Meeting type"
                  onChange={(e) => setMeetingForm({ ...meetingForm, meetingType: e.target.value })}
                >
                  <MenuItem value="one-time">One-time</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </FormControl>
              {meetingForm.meetingType === "recurring" && (
                <>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Recurrence pattern</InputLabel>
                    <Select
                      value={meetingForm.recurrencePattern}
                      label="Recurrence pattern"
                      onChange={(e) =>
                        setMeetingForm({ ...meetingForm, recurrencePattern: e.target.value })
                      }
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="absoluteMonthly">Monthly</MenuItem>
                      <MenuItem value="relativeMonthly">Relative monthly</MenuItem>
                      <MenuItem value="absoluteYearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Interval"
                    type="number"
                    fullWidth
                    value={meetingForm.recurrenceInterval}
                    onChange={(e) =>
                      setMeetingForm({ ...meetingForm, recurrenceInterval: e.target.value })
                    }
                  />
                </>
              )}
            </Stack>
            {meetingForm.meetingType === "recurring" && (
              <TextField
                label="Recurrence end date"
                type="date"
                fullWidth
                value={meetingForm.recurrenceEndDate}
                onChange={(e) =>
                  setMeetingForm({ ...meetingForm, recurrenceEndDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={meetingForm.sendEmail}
                  onChange={(e) => setMeetingForm({ ...meetingForm, sendEmail: e.target.checked })}
                />
              }
              label="Send invitation email with .ics attachment"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton onClick={() => setMeetingDialogOpen(false)} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleCreateMeeting} color="info" disabled={meetingSubmitting}>
            {meetingSubmitting ? "Creating..." : "Create meeting"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Row actions menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={openRescheduleDialog}>Reschedule</MenuItem>
        <MenuItem onClick={openDeleteDialog}>Delete</MenuItem>
      </Menu>

      {/* Reschedule dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={() => setRescheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0078d4 0%, #2b6cb0 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          Reschedule meeting
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
              {dialogError}
            </MDAlert>
          )}
          <Stack spacing={2} mt={1}>
            <MDTypography variant="body2" color="text">
              {selectedMeeting?.subject}
            </MDTypography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="New start date/time"
                type="datetime-local"
                fullWidth
                value={rescheduleForm.startDateTime}
                onChange={(e) =>
                  setRescheduleForm({ ...rescheduleForm, startDateTime: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="New end date/time"
                type="datetime-local"
                fullWidth
                value={rescheduleForm.endDateTime}
                onChange={(e) =>
                  setRescheduleForm({ ...rescheduleForm, endDateTime: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Time zone"
              fullWidth
              value={rescheduleForm.timeZone}
              onChange={(e) => setRescheduleForm({ ...rescheduleForm, timeZone: e.target.value })}
              helperText="The new start and end times are in this zone."
            />
            {selectedMeeting?.legacyTimes && (
              <MDTypography variant="caption" color="text">
                This meeting was saved before times followed the Time zone box, so its time above is
                worked out from what Teams was sent. If it doesn&apos;t match Outlook, go by
                Outlook.
              </MDTypography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={rescheduleForm.sendEmail}
                  onChange={(e) =>
                    setRescheduleForm({ ...rescheduleForm, sendEmail: e.target.checked })
                  }
                />
              }
              label="Email attendees the updated time"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton onClick={() => setRescheduleDialogOpen(false)} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleRescheduleSubmit} color="info" disabled={rescheduleSubmitting}>
            {rescheduleSubmitting ? "Saving..." : "Save new time"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #f44336 0%, #c62828 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} color="inherit">
            <Icon>warning</Icon>
            <span>Confirm Delete</span>
          </MDBox>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          {dialogError && (
            <MDBox mb={2}>
              <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
                {dialogError}
              </MDAlert>
            </MDBox>
          )}
          <MDTypography variant="body2">
            Are you sure you want to delete <strong>{selectedMeeting?.subject}</strong>? This
            cancels the meeting in Microsoft Teams and notifies attendees. If it is a Clarity Call,
            its booking is cancelled too.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialogOpen(false)} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteConfirm} color="error" disabled={deleteSubmitting}>
            {deleteSubmitting ? "Deleting..." : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Meetings;
